import { createSlice } from '@reduxjs/toolkit';
import { request } from 'modules/Api/HttpClient';
import {
  STUDENT_BY_ID_URL,
  STUDENTS_AVAILABLE_TESTS_URL,
  DASHBOARD_INDIVIDUAL_STUDENT_PERFORMANCE_URL,
} from 'modules/Api/Routes';

const initialState = {
  loading: false,
  error: null,
  student: {
    data: null,
    loading: false,
  },
  availableTests: {
    data: [],
    loading: false,
    error: null,
  },
  performanceData: {
    data: null,
    loading: false,
    error: null,
  },
};

const studentProfileSlice = createSlice({
  name: 'studentProfile',
  initialState,
  reducers: {
    cleanState: () => ({ ...initialState }),

    requestStudent: (state) => {
      state.student.loading = true;
      state.error = null;
    },
    receiveStudent: (state, action) => {
      state.student.loading = false;
      state.student.data = action.payload;
    },
    receiveStudentError: (state, action) => {
      state.student.loading = false;
      state.error = action.payload;
    },

    requestAvailableTests: (state) => {
      state.availableTests.loading = true;
      state.availableTests.error = null;
    },
    receiveAvailableTests: (state, action) => {
      state.availableTests.loading = false;
      state.availableTests.data = action.payload;
    },
    receiveAvailableTestsError: (state, action) => {
      state.availableTests.loading = false;
      state.availableTests.error = action.payload;
    },

    requestPerformanceData: (state) => {
      state.performanceData.loading = true;
      state.performanceData.error = null;
    },
    receivePerformanceData: (state, action) => {
      state.performanceData.loading = false;
      state.performanceData.data = action.payload;
    },
    receivePerformanceDataError: (state, action) => {
      state.performanceData.loading = false;
      state.performanceData.error = action.payload;
    },
  },
});

const Actions = studentProfileSlice.actions;

const Selectors = {
  fetchProfileData: (state) => state.studentProfile,
};

const Async = {
  fetchStudentById:
    ({ studentId }) =>
    async (dispatch) => {
      dispatch(Actions.requestStudent());

      try {
        const response = await request({
          method: 'GET',
          url: `${STUDENT_BY_ID_URL}?id=${studentId}`,
        });

        dispatch(Actions.receiveStudent(response.data.content));
      } catch (e) {
        dispatch(Actions.receiveStudentError(e.message));
      }
    },

  fetchAvailableTests:
    ({ studentId }) =>
    async (dispatch) => {
      dispatch(Actions.requestAvailableTests());

      try {
        const response = await request({
          method: 'GET',
          url: STUDENTS_AVAILABLE_TESTS_URL,
          params: { student_id: studentId },
        });

        const availableTests = response.data.content?.available_tests || [];
        dispatch(Actions.receiveAvailableTests(availableTests));

        return availableTests;
      } catch (e) {
        dispatch(Actions.receiveAvailableTestsError(e.message));
        return [];
      }
    },

  fetchPerformanceData:
    ({ studentId, testId, days = 7, startDate, endDate }) =>
    async (dispatch) => {
      dispatch(Actions.requestPerformanceData());

      try {
        const params = {
          student_id: studentId,
          test_id: testId,
        };

        // Envia o intervalo customizado quando presente; caso contrário, days.
        // Mantê-los mutuamente exclusivos evita que o backend priorize days.
        if (startDate && endDate) {
          params.start_date = startDate;
          params.end_date = endDate;
        } else {
          params.days = days;
        }

        const response = await request({
          method: 'GET',
          url: DASHBOARD_INDIVIDUAL_STUDENT_PERFORMANCE_URL,
          params,
        });

        dispatch(Actions.receivePerformanceData(response.data.content));
      } catch (e) {
        dispatch(Actions.receivePerformanceDataError(e.message));
      }
    },

  fetchAllProfileData:
    ({ studentId }) =>
    async (dispatch) => {
      dispatch(Async.fetchStudentById({ studentId }));

      // Buscar testes disponíveis
      // A busca de performance será feita pelo componente quando o teste for selecionado
      await dispatch(Async.fetchAvailableTests({ studentId }));
    },
};

const { reducer } = studentProfileSlice;

export { reducer, Actions, Async, Selectors };
